import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight, Mail, Send } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useI18n } from "@/lib/i18n";
import { useContent } from "@/lib/content";

export default function PaymentSuccessPage() {
  const { lang } = useI18n();
  const { content } = useContent();
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || params.get("orderId") || params.get("id") || params.get("transaction_id");
  const amount = params.get("amount") || params.get("sum");
  const c = content?.contacts || {};

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-24 sm:pt-32 pb-16 sm:pb-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="inline-flex size-20 rounded-full bg-accent/15 items-center justify-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <CheckCircle2 className="size-12 text-accent" strokeWidth={2.5} />
              </motion.div>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4" data-testid="payment-success-title">
              {lang === "ru" ? "Оплата прошла успешно" : "Payment successful"}
            </h1>
            <p className="text-base text-muted-foreground mb-8 leading-relaxed">
              {lang === "ru"
                ? "Спасибо за оплату! Менеджер TEMPEO свяжется с вами в течение рабочего дня для подтверждения и следующих шагов."
                : "Thank you for your payment! A TEMPEO manager will contact you within one business day to confirm and discuss next steps."}
            </p>
            {(orderId || amount) && (
              <div className="inline-flex flex-col items-start gap-1 p-4 rounded-2xl border border-border bg-card text-left mb-8" data-testid="payment-success-details">
                {orderId && <div className="text-sm"><span className="text-muted-foreground">{lang === "ru" ? "Номер платежа:" : "Order ID:"}</span> <span className="font-semibold ml-2">{orderId}</span></div>}
                {amount && <div className="text-sm"><span className="text-muted-foreground">{lang === "ru" ? "Сумма:" : "Amount:"}</span> <span className="font-semibold ml-2">{amount}</span></div>}
              </div>
            )}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 mb-10">
              <Link to="/" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-foreground text-background font-semibold text-sm hover:translate-y-[-2px] transition-transform" data-testid="payment-success-home">
                {lang === "ru" ? "На главную" : "Back home"}
                <ArrowRight className="size-4" />
              </Link>
              {c.telegram_link && (
                <a href={c.telegram_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full border border-border text-foreground font-semibold text-sm hover:bg-muted">
                  <Send className="size-4" />
                  {lang === "ru" ? "Написать менеджеру" : "Message manager"}
                </a>
              )}
            </div>
            {c.email && (
              <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
                <Mail className="size-4" />
                {lang === "ru" ? "Вопросы:" : "Questions:"} <a className="text-foreground underline underline-offset-2" href={`mailto:${c.email}`}>{c.email}</a>
              </p>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
